import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import SettingsIcon from "@mui/icons-material/Settings";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import axios from "axios";
import RichTextEditor from "./RichTextEditor";
import RecipientsField, { EMAIL_REGEX } from "./RecipientsField";
import PlaceholderBar from "./PlaceholderBar";
import ConfirmDialog from "./ConfirmDialog";
import TemplateManagerDialog from "./TemplateManagerDialog";
import RecipientManagerDialog from "./RecipientManagerDialog";
import useEmailTemplates from "./useEmailTemplates";
import useEmailRecipients from "./useEmailRecipients";
import usePlaceholderInsert from "./usePlaceholderInsert";
import { renderEmailPreview } from "./emailPreview";
import {
  fetchEmailPackages,
  fetchEmailPackage,
  saveRecipientsBulk,
  getApiError,
} from "./emailApi";
import {
  RECIPIENT_SPECIFIC_KEYS,
  extractPlaceholders,
  findMissingPackagePlaceholders,
  findUnknownPlaceholders,
  isBodyEmpty,
  isTemplateAvailableForContractor,
  toToken,
} from "./emailUtils";

const MAX_FILES = 10;
const MAX_TOTAL_BYTES = 20 * 1024 * 1024; // keep in sync with backend / mail provider limit

const formatBytes = (bytes) =>
  bytes < 1024 * 1024
    ? `${Math.max(1, Math.round(bytes / 1024))} KB`
    : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

const fileKey = (f) => `${f.name}-${f.size}-${f.lastModified}`;

const uniqueEmails = (emails) => {
  const seen = new Set();
  return emails
    .filter((email) => typeof email === "string")
    .map((email) => email.trim())
    .filter((email) => {
      const key = email.toLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
};

const ComposeEmailForm = ({ currentSession }) => {
  const [to, setTo] = useState([]);
  const [manualCc, setManualCc] = useState([]);
  const [packageCc, setPackageCc] = useState([]);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    severity: "success",
    message: "",
  });
  const fileInputRef = useRef(null);
  const sendingRef = useRef(false);

  // Keep the selected package contractor email separate so changing packages
  // cannot remove addresses the admin entered manually.
  const cc = useMemo(
    () => uniqueEmails([...manualCc, ...packageCc]),
    [manualCc, packageCc],
  );
  const handleCcChange = (emails) => {
    const nextKeys = new Set(emails.map((email) => email.toLowerCase()));
    const automaticKeys = new Set(
      packageCc.map((email) => email.toLowerCase()),
    );
    setManualCc(
      uniqueEmails([
        ...manualCc.filter((email) => nextKeys.has(email.toLowerCase())),
        ...emails.filter((email) => !automaticKeys.has(email.toLowerCase())),
      ]),
    );
    setPackageCc((prev) =>
      prev.filter((email) => nextKeys.has(email.toLowerCase())),
    );
  };

  // Package lookup is lazy: sending without a package does not require it.
  const [packagesOpen, setPackagesOpen] = useState(false);
  const [packageSearch, setPackageSearch] = useState("");
  const [packageOptions, setPackageOptions] = useState([]);
  const [packagesLoading, setPackagesLoading] = useState(false);
  const [packagesError, setPackagesError] = useState("");
  const [packagesRetry, setPackagesRetry] = useState(0);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [packageResult, setPackageResult] = useState(null);
  const [packageLoading, setPackageLoading] = useState(false);
  const [packageError, setPackageError] = useState("");
  const [packageRetry, setPackageRetry] = useState(0);
  const [missingPackageConfirm, setMissingPackageConfirm] = useState(false);
  const packageRequestRef = useRef(null);
  const selectedPackageId = selectedPackage?.id ?? null;
  const packageContext =
    selectedPackageId !== null &&
    packageResult?.currentSession === currentSession &&
    String(packageResult?.data?.id) === String(selectedPackageId)
      ? packageResult.data
      : null;
  const packageReady =
    !selectedPackage ||
    (Boolean(packageContext) && !packageLoading && !packageError);
  const contractorId = packageContext?.contractor?.id ?? null;
  const packageValues = useMemo(
    () => packageContext?.placeholders || {},
    [packageContext],
  );

  useEffect(() => {
    const searchValue = packageSearch.trim();
    if (!packagesOpen || !searchValue) {
      setPackageOptions([]);
      setPackagesLoading(false);
      setPackagesError("");
      return;
    }
    const controller = new AbortController();
    let active = true;
    setPackagesLoading(true);
    setPackagesError("");
    setPackageOptions([]);

    const timer = setTimeout(async () => {
      try {
        const results = await fetchEmailPackages(currentSession, {
          search: searchValue,
          limit: 20,
          signal: controller.signal,
        });
        if (active) setPackageOptions(results);
      } catch (e) {
        if (active) {
          setPackagesError(getApiError(e, "Failed to load permit packages."));
        }
      } finally {
        if (active) setPackagesLoading(false);
      }
    }, 300);

    return () => {
      active = false;
      clearTimeout(timer);
      controller.abort();
    };
  }, [packagesOpen, currentSession, packageSearch, packagesRetry]);

  useEffect(() => {
    if (selectedPackageId === null) return;
    const controller = new AbortController();
    packageRequestRef.current = controller;
    setPackageLoading(true);
    setPackageError("");
    setPackageResult(null);
    setPackageCc([]);

    const loadPackage = async () => {
      try {
        const data = await fetchEmailPackage(
          currentSession,
          selectedPackageId,
          {
            signal: controller.signal,
          },
        );
        if (controller.signal.aborted) return;
        if (String(data.id) !== String(selectedPackageId)) {
          throw new Error(
            "The returned permit package does not match the selection.",
          );
        }
        setPackageResult({ currentSession, data });
        setPackageCc(uniqueEmails([data.contractor?.email]));
      } catch (e) {
        if (!controller.signal.aborted) {
          setPackageError(
            getApiError(e, "Failed to load permit package details."),
          );
        }
      } finally {
        if (!controller.signal.aborted) setPackageLoading(false);
      }
    };
    loadPackage();

    return () => controller.abort();
  }, [selectedPackageId, currentSession, packageRetry]);

  const handlePackageChange = (_, nextPackage) => {
    if (sendingRef.current) return;
    if (String(nextPackage?.id ?? "") === String(selectedPackageId ?? "")) {
      return;
    }
    packageRequestRef.current?.abort();
    setSelectedPackage(nextPackage);
    setPackageResult(null);
    setPackageCc([]);
    setPackageError("");
    setPackageLoading(Boolean(nextPackage));
    setPackageSearch("");
    setMissingPackageConfirm(false);
    setPendingTemplate(null);
  };

  const packagePickerOptions = selectedPackage
    ? [
        selectedPackage,
        ...packageOptions.filter(
          (item) => String(item.id) !== String(selectedPackage.id),
        ),
      ]
    : packageOptions;

  // Templates
  const {
    templates,
    loading: templatesLoading,
    error: templatesError,
    reload: reloadTemplates,
    upsert: upsertTemplate,
    remove: removeTemplate,
  } = useEmailTemplates(currentSession);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [pendingTemplate, setPendingTemplate] = useState(null);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [templateScopeNotice, setTemplateScopeNotice] = useState("");

  const availableTemplates = useMemo(
    () =>
      templates.filter((t) =>
        isTemplateAvailableForContractor(t, contractorId),
      ),
    [templates, contractorId],
  );

  useEffect(() => {
    // Wait for the selected package's contractor before evaluating its scope.
    if (!packageReady || templatesLoading) return;
    const template = templates.find((t) => t.id === selectedTemplateId);
    if (template && !isTemplateAvailableForContractor(template, contractorId)) {
      setSelectedTemplateId(null);
      setTemplateScopeNotice(
        "The selected template is not available for this package context. Its selection was cleared, but your draft was kept. Review the subject and body before sending.",
      );
    }
    if (
      pendingTemplate &&
      !isTemplateAvailableForContractor(pendingTemplate, contractorId)
    ) {
      setPendingTemplate(null);
    }
  }, [
    packageReady,
    templatesLoading,
    templates,
    selectedTemplateId,
    contractorId,
    pendingTemplate,
  ]);

  // Saved recipients
  const {
    recipients,
    byEmail,
    loading: recipientsLoading,
    error: recipientsError,
    reload: reloadRecipients,
    upsert: upsertRecipient,
    upsertMany: upsertRecipients,
    remove: removeRecipient,
  } = useEmailRecipients(currentSession);
  const [recipientsOpen, setRecipientsOpen] = useState(false);
  const [savingRecipients, setSavingRecipients] = useState(false);

  const selectedTemplate = useMemo(
    () => availableTemplates.find((t) => t.id === selectedTemplateId) || null,
    [availableTemplates, selectedTemplateId],
  );

  const { subjectRef, handleEditorReady, insertPlaceholder } =
    usePlaceholderInsert(subject, setSubject);

  const usedPlaceholders = useMemo(
    () => extractPlaceholders(subject, body),
    [subject, body],
  );

  const unknownPlaceholders = useMemo(
    () => findUnknownPlaceholders(subject, body),
    [subject, body],
  );

  const missingPackagePlaceholders = findMissingPackagePlaceholders(
    packageValues,
    subject,
    body,
  );
  const usesRecipientPlaceholders = usedPlaceholders.some((k) =>
    RECIPIENT_SPECIFIC_KEYS.has(k),
  );
  const usesRecipientName = usedPlaceholders.includes("recipient_name");
  const recipientsReady = !recipientsLoading && !recipientsError;
  const previewRecipientEmail = to[0] || "";
  const previewRecipientName = previewRecipientEmail
    ? byEmail.get(previewRecipientEmail.toLowerCase())?.name || ""
    : "";
  const emailPreview = useMemo(
    () =>
      renderEmailPreview({
        subject,
        body,
        recipientEmail: previewRecipientEmail,
        recipientName: previewRecipientName,
        packageValues,
      }),
    [subject, body, previewRecipientEmail, previewRecipientName, packageValues],
  );
  const hasReviewContent = Boolean(subject.trim() || !isBodyEmpty(body));

  // "To" addresses with no saved name (only matters if {{recipient_name}} is used)
  const emailsWithoutName = useMemo(
    () =>
      usesRecipientName && recipientsReady
        ? to.filter((e) => !byEmail.get(e.toLowerCase())?.name)
        : [],
    [usesRecipientName, recipientsReady, to, byEmail],
  );

  // Valid To/CC addresses that aren't in the saved list yet
  const unsavedEmails = useMemo(() => {
    if (!recipientsReady) return [];
    const seen = new Set();
    const out = [];
    [...to, ...cc].forEach((e) => {
      const key = e.toLowerCase();
      if (EMAIL_REGEX.test(e) && !byEmail.has(key) && !seen.has(key)) {
        seen.add(key);
        out.push(e);
      }
    });
    return out;
  }, [to, cc, byEmail, recipientsReady]);

  const notify = (severity, message) =>
    setToast({ open: true, severity, message });

  const applyTemplate = (template) => {
    if (
      !packageReady ||
      !isTemplateAvailableForContractor(template, contractorId)
    ) {
      notify(
        "error",
        "This template is not available for the selected package.",
      );
      return;
    }
    setSubject(template.subject || "");
    setBody(template.body || "");
    setSelectedTemplateId(template.id);
    setTemplateScopeNotice("");
  };

  const handleTemplateChange = (_, template) => {
    if (!template) {
      // "No template": deselect only, keep whatever is currently written
      setSelectedTemplateId(null);
      return;
    }
    if (subject.trim() || !isBodyEmpty(body)) {
      setPendingTemplate(template); // ask before overwriting
    } else {
      applyTemplate(template);
    }
  };

  const handleTemplateDeleted = (id) => {
    removeTemplate(id);
    if (id === selectedTemplateId) setSelectedTemplateId(null);
    if (id === pendingTemplate?.id) setPendingTemplate(null);
  };

  const handleSaveNewRecipients = async () => {
    try {
      setSavingRecipients(true);
      const saved = await saveRecipientsBulk(
        currentSession,
        unsavedEmails.map((email) => ({ email })),
      );
      upsertRecipients(saved);
      notify(
        "success",
        `Saved ${saved.length} address${saved.length === 1 ? "" : "es"}.`,
      );
    } catch (e) {
      notify("error", getApiError(e, "Failed to save recipients."));
    } finally {
      setSavingRecipients(false);
    }
  };

  const handleFiles = (e) => {
    const incoming = Array.from(e.target.files || []);
    e.target.value = ""; // allow re-selecting the same file

    const existing = new Set(attachments.map(fileKey));
    const next = [
      ...attachments,
      ...incoming.filter((f) => !existing.has(fileKey(f))),
    ];

    if (next.length > MAX_FILES) {
      notify("error", `You can attach up to ${MAX_FILES} files.`);
      return;
    }
    if (next.reduce((sum, f) => sum + f.size, 0) > MAX_TOTAL_BYTES) {
      notify(
        "error",
        `Total attachment size cannot exceed ${formatBytes(MAX_TOTAL_BYTES)}.`,
      );
      return;
    }
    setAttachments(next);
  };

  const removeAttachment = (key) =>
    setAttachments((prev) => prev.filter((f) => fileKey(f) !== key));

  const resetForm = () => {
    packageRequestRef.current?.abort();
    setTo([]);
    setManualCc([]);
    setPackageCc([]);
    setSubject("");
    setBody("");
    setAttachments([]);
    setSelectedTemplateId(null);
    setPendingTemplate(null);
    setSelectedPackage(null);
    setPackageResult(null);
    setPackageLoading(false);
    setPackageError("");
    setPackagesOpen(false);
    setPackageSearch("");
    setPackageOptions([]);
    setPackagesError("");
    setTemplateScopeNotice("");
    setMissingPackageConfirm(false);
    setSubmitted(false);
  };

  // An acknowledgement applies only to the current draft and package context.
  useEffect(() => {
    setMissingPackageConfirm(false);
  }, [
    subject,
    body,
    to,
    cc,
    attachments,
    selectedTemplateId,
    packageResult,
    currentSession,
  ]);

  const handleSend = async (allowMissingPackagePlaceholders = false) => {
    if (sendingRef.current) return;
    setSubmitted(true);

    if (!packageReady) {
      setMissingPackageConfirm(false);
      return notify(
        "error",
        packageError
          ? "Retry loading the package details or clear the package before sending."
          : "Wait for the permit package details to finish loading.",
      );
    }
    const template = templates.find((item) => item.id === selectedTemplateId);
    if (template && !isTemplateAvailableForContractor(template, contractorId)) {
      return notify(
        "error",
        "Select a template available for this package or use no template.",
      );
    }
    if (to.length === 0) return notify("error", "Add at least one recipient.");
    if ([...to, ...cc].some((e) => !EMAIL_REGEX.test(e)))
      return notify("error", "Fix the invalid email addresses.");
    if (!subject.trim()) return notify("error", "Subject is required.");
    if (isBodyEmpty(body)) return notify("error", "Email body is empty.");
    if (unknownPlaceholders.length)
      return notify(
        "error",
        `Unknown placeholders: ${unknownPlaceholders.map(toToken).join(", ")}`,
      );

    if (missingPackagePlaceholders.length && !allowMissingPackagePlaceholders) {
      setMissingPackageConfirm(true);
      return;
    }
    setMissingPackageConfirm(false);

    const formData = new FormData();
    formData.append("to", JSON.stringify(to));
    formData.append("cc", JSON.stringify(cc));
    formData.append("subject", subject.trim());
    formData.append("body", body); // HTML, edited version (not the saved template)
    formData.append("currentSession", currentSession);
    if (selectedTemplateId) formData.append("templateId", selectedTemplateId); // metadata only
    // New send contract: resolve package tokens server-side from the trusted ID.
    // Do not send preview values or re-add contractor CCs: cc is the admin's final list.
    if (selectedPackageId !== null) {
      formData.append("packageId", selectedPackageId);
    }
    // Backend must reject missing package values unless explicitly acknowledged.
    // When acknowledged, replace unresolved package tokens with empty strings.
    if (allowMissingPackagePlaceholders && missingPackagePlaceholders.length) {
      formData.append("allowMissingPackagePlaceholders", "true");
    }
    attachments.forEach((file) => formData.append("attachments", file));

    try {
      sendingRef.current = true;
      setSending(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/admin/email/send`,
        formData,
        { withCredentials: true },
      );
      if (res.data?.success) {
        notify("success", res.data?.message || "Email sent.");
        resetForm();
      } else {
        notify("error", res.data?.message || "Failed to send email.");
      }
    } catch (err) {
      notify("error", err?.response?.data?.message || "Failed to send email.");
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const totalBytes = attachments.reduce((sum, f) => sum + f.size, 0);

  return (
    <Paper sx={{ p: { xs: 2, md: 3 } }}>
      <Stack
        component="fieldset"
        disabled={sending}
        spacing={2.5}
        sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}
      >
        {templatesError && (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={reloadTemplates}>
                Retry
              </Button>
            }
          >
            {templatesError}
          </Alert>
        )}
        {recipientsError && (
          <Alert
            severity="warning"
            action={
              <Button color="inherit" size="small" onClick={reloadRecipients}>
                Retry
              </Button>
            }
          >
            {recipientsError} You can still type addresses manually.
          </Alert>
        )}

        {packagesError && (
          <Alert
            severity="warning"
            action={
              <Button
                color="inherit"
                size="small"
                disabled={packagesLoading || sending}
                onClick={() => {
                  setPackagesOpen(true);
                  setPackagesRetry((value) => value + 1);
                }}
              >
                Retry
              </Button>
            }
          >
            {packagesError} You can still send without selecting a package.
          </Alert>
        )}
        <Autocomplete
          fullWidth
          open={packagesOpen}
          onOpen={() => setPackagesOpen(true)}
          onClose={() => setPackagesOpen(false)}
          options={packagePickerOptions}
          value={selectedPackage}
          loading={packagesLoading}
          disabled={sending}
          onChange={handlePackageChange}
          onInputChange={(_, value, reason) => {
            if (reason === "input" || reason === "clear") {
              setPackageSearch(value);
            }
          }}
          filterOptions={(options) => options}
          getOptionLabel={(option) => option.label || "Permit package"}
          isOptionEqualToValue={(option, value) =>
            String(option.id) === String(value.id)
          }
          noOptionsText={
            packagesError
              ? "Packages could not be loaded"
              : packageSearch.trim()
                ? "No packages found"
                : "Start typing to search permit packages"
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label="Permit package (optional)"
              helperText="Start typing a contractor name, owner name, or property address. Up to 20 matching packages are shown."
            />
          )}
        />
        {selectedPackage && !packageReady && !packageError && (
          <Alert severity="info">Loading permit package details…</Alert>
        )}
        {packageError && (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                disabled={packageLoading || sending}
                onClick={() => setPackageRetry((value) => value + 1)}
              >
                Retry
              </Button>
            }
          >
            {packageError} Retry or clear the selected package to send without
            it.
          </Alert>
        )}
        {packageContext && (
          <Alert severity="info">
            Package:{" "}
            {packageContext.label || selectedPackage.label || "Permit package"}.
            Contractor: {packageContext.contractor?.name || "Not available"}.{" "}
            The contractor&apos;s primary email has been added below when
            available; you can remove it. Changing or clearing the package
            removes its automatic CC and keeps manually entered addresses.
          </Alert>
        )}
        {templateScopeNotice && (
          <Alert severity="warning" onClose={() => setTemplateScopeNotice("")}>
            {templateScopeNotice}
          </Alert>
        )}

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1.5}
          alignItems="flex-start"
        >
          <Autocomplete
            fullWidth
            options={availableTemplates}
            loading={templatesLoading}
            disabled={sending || !packageReady}
            value={selectedTemplate}
            onChange={handleTemplateChange}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            noOptionsText="No templates available for this package context"
            renderInput={(params) => (
              <TextField
                {...params}
                label="Template (optional)"
                helperText={`Fills subject and body without changing the saved template. ${
                  contractorId !== null
                    ? "Showing global and this contractor's templates."
                    : "Showing global templates; select a package to use contractor-specific templates."
                }`}
              />
            )}
          />
          <Button
            variant="outlined"
            startIcon={<SettingsIcon />}
            onClick={() => setTemplatesOpen(true)}
            sx={{ whiteSpace: "nowrap", height: 56 }}
          >
            Manage templates
          </Button>
        </Stack>

        <RecipientsField
          label="To"
          required
          value={to}
          onChange={setTo}
          options={recipients}
          loading={recipientsLoading}
          error={submitted && to.length === 0}
          helperText={
            submitted && to.length === 0
              ? "At least one recipient is required"
              : undefined
          }
        />
        <RecipientsField
          label="CC (optional)"
          value={cc}
          onChange={handleCcChange}
          options={recipients}
          loading={recipientsLoading}
          helperText={
            packageContext
              ? "Any saved contractor CCs are pre-filled. Remove any address you do not want copied."
              : undefined
          }
        />

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Button
            size="small"
            startIcon={<ContactMailIcon />}
            onClick={() => setRecipientsOpen(true)}
          >
            Manage saved recipients
          </Button>
          {unsavedEmails.length > 0 && (
            <LoadingButton
              size="small"
              loading={savingRecipients}
              onClick={handleSaveNewRecipients}
            >
              Save {unsavedEmails.length} new address
              {unsavedEmails.length === 1 ? "" : "es"}
            </LoadingButton>
          )}
        </Stack>

        <TextField
          label="Subject"
          required
          fullWidth
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          inputRef={subjectRef}
          error={submitted && !subject.trim()}
          helperText={
            submitted && !subject.trim() ? "Subject is required" : undefined
          }
        />

        <RichTextEditor
          value={body}
          onChange={setBody}
          onEditorReady={handleEditorReady}
        />

        <Stack spacing={1}>
          <PlaceholderBar onInsert={insertPlaceholder} />
          {unknownPlaceholders.length > 0 && (
            <Alert severity="warning">
              Unknown placeholders:{" "}
              {unknownPlaceholders.map(toToken).join(", ")}
            </Alert>
          )}
          {packageReady && missingPackagePlaceholders.length > 0 && (
            <Alert severity="warning">
              Missing package values:{" "}
              {missingPackagePlaceholders.map(toToken).join(", ")}.{" "}
              {selectedPackage
                ? "The selected package is missing this data."
                : "No permit package is selected."}{" "}
              Select a package, edit these placeholders, or confirm sending with
              these values left empty.
            </Alert>
          )}
          {hasReviewContent && (
            <Box
              component="section"
              aria-labelledby="email-review-heading"
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                overflow: "hidden",
                bgcolor: "background.paper",
              }}
            >
              <Box
                sx={{
                  px: 2,
                  py: 1.25,
                  bgcolor: "action.hover",
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <Typography
                  id="email-review-heading"
                  variant="subtitle1"
                  fontWeight={700}
                >
                  Review
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Preview of the email as the first To recipient will receive
                  it.
                </Typography>
              </Box>
              <Stack spacing={1.5} sx={{ p: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    To
                  </Typography>
                  <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>
                    {previewRecipientEmail || "No recipient selected"}
                    {previewRecipientName ? ` — ${previewRecipientName}` : ""}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Subject
                  </Typography>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    sx={{ overflowWrap: "anywhere" }}
                  >
                    {emailPreview.subject || "No subject"}
                  </Typography>
                </Box>
                <Box sx={{ pt: 1.5, borderTop: 1, borderColor: "divider" }}>
                  <Typography variant="caption" color="text.secondary">
                    Message
                  </Typography>
                  <Box
                    sx={{
                      mt: 1,
                      overflowWrap: "anywhere",
                      "& p": { my: 1 },
                      "& p:first-of-type": { mt: 0 },
                      "& p:last-of-type": { mb: 0 },
                    }}
                    dangerouslySetInnerHTML={{ __html: emailPreview.bodyHtml }}
                  />
                </Box>
                {usesRecipientPlaceholders && to.length > 1 && (
                  <Typography variant="caption" color="text.secondary">
                    Showing {previewRecipientEmail}. Recipient placeholders are
                    personalized separately for each of the {to.length} To
                    recipients.
                  </Typography>
                )}
              </Stack>
            </Box>
          )}
          {emailsWithoutName.length > 0 && (
            <Alert severity="warning">
              No saved name for {emailsWithoutName.join(", ")}.{" "}
              {toToken("recipient_name")} will be empty for them. Add names in
              "Manage saved recipients".
            </Alert>
          )}
          {usesRecipientPlaceholders && to.length > 1 && (
            <Alert severity="info">
              This email uses recipient placeholders, so it will be sent as{" "}
              {to.length} separate emails (one per "To" address). CC recipients
              are copied on each.
            </Alert>
          )}
        </Stack>

        <Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AttachFileIcon />}
            onClick={() => fileInputRef.current?.click()}
          >
            Attach files
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            hidden
            onChange={handleFiles}
          />
          {attachments.length > 0 && (
            <>
              <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1.5 }}>
                {attachments.map((f) => (
                  <Chip
                    key={fileKey(f)}
                    label={`${f.name} (${formatBytes(f.size)})`}
                    onDelete={() => removeAttachment(fileKey(f))}
                  />
                ))}
              </Stack>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 0.5, display: "block" }}
              >
                {attachments.length}/{MAX_FILES} files ·{" "}
                {formatBytes(totalBytes)} of {formatBytes(MAX_TOTAL_BYTES)}
              </Typography>
            </>
          )}
        </Box>

        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button onClick={resetForm} disabled={sending}>
            Clear
          </Button>
          <LoadingButton
            variant="contained"
            endIcon={<SendIcon />}
            loading={sending}
            disabled={!packageReady}
            onClick={() => handleSend()}
          >
            Send
          </LoadingButton>
        </Stack>
      </Stack>

      <TemplateManagerDialog
        open={templatesOpen}
        onClose={() => setTemplatesOpen(false)}
        templates={templates}
        loading={templatesLoading}
        error={templatesError}
        onRetry={reloadTemplates}
        currentSession={currentSession}
        onSaved={upsertTemplate}
        onDeleted={handleTemplateDeleted}
      />

      <RecipientManagerDialog
        open={recipientsOpen}
        onClose={() => setRecipientsOpen(false)}
        recipients={recipients}
        loading={recipientsLoading}
        error={recipientsError}
        onRetry={reloadRecipients}
        currentSession={currentSession}
        onSaved={upsertRecipient}
        onDeleted={removeRecipient}
      />

      <ConfirmDialog
        open={missingPackageConfirm}
        title="Send with missing package values?"
        message={`These placeholders cannot be filled: ${missingPackagePlaceholders.map(toToken).join(", ")}. ${selectedPackage ? "The selected package is missing this data." : "No permit package is selected."} If you continue, these values will be left empty, which may omit the contractor name or signing link. Cancel to select a package or edit the email.`}
        confirmLabel="Send with empty values"
        confirmColor="warning"
        loading={sending}
        onConfirm={() => handleSend(true)}
        onCancel={() => setMissingPackageConfirm(false)}
      />

      <ConfirmDialog
        open={Boolean(pendingTemplate)}
        title="Replace current content?"
        message="Loading this template will replace the subject and body you've written."
        confirmLabel="Replace"
        onConfirm={() => {
          applyTemplate(pendingTemplate);
          setPendingTemplate(null);
        }}
        onCancel={() => setPendingTemplate(null)}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ mt: { xs: 7, sm: 8 } }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          sx={{
            color: "common.white",
            "& .MuiAlert-icon": { color: "common.white" },
          }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default ComposeEmailForm;
